import {
  IPettyCashCategory,
  PettyCashFormProps,
} from "../../../../../Types/PettyCash";
import {
  numeric,
  PettyCashExpenseModal,
  submit,
  Submit,
  success,
  TwoDigit,
} from "../../../../../utils/Constant";
import { createFormData } from "../../../../../utils/formUtils";
import { Col, Form, Row } from "reactstrap";
import { Btn } from "../../../../../AbstractElements";
import { pettyCashExpenseValidationSchema } from "../../validationSchema";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { pettyCashFormFields } from "./pettyCashFormFields";
import { DynamicFieldRenderer } from "../../../../../CommonElements/DynamicFieldRenderer";
import { Nullable } from "../../../../../Types/Common";
import { useAddPettyCashExpenseMutation } from "../../../../../ReduxToolkit/api/pettyCash";
import { showApiResultAlert } from "../../../../../utils/AlertManager/showApiAlert";
import useUser from "../../../../../hooks/useUser";
import { canAddBackdatedExpenses, getExpenseMonthStart } from "../../../../../utils/validation/expenseDateValidation";

const initialValues = {
  categoryType: "",
  amount: 0,
  paymentMode: "",
  pettyCashDate: new Date(),
  description: "",
  pettyCashAttachment: null,
};

type PettyCashFormPayload = {
  pettyCashDate: Date;
  categoryType: string;
  amount: number;
  paymentMode: string;
  pettyCashAttachment: Nullable<any>;
  description: string;
};

const useDynamicOptions = (categoriesType: Array<IPettyCashCategory>) => {
  return {
    categoryType:
      categoriesType && categoriesType?.length > 0
        ? categoriesType.map((d) => ({ value: d.id.toString(), label: d.name }))
        : [],
  };
};

const ManagerZonePettyCashForm = ({
  userId,
  expenseCategoryTypeOptions,
}: PettyCashFormProps) => {
  const today = new Date();
  const monthStart = getExpenseMonthStart(today);
  const loggedInEmployee = useUser();
  const loggedInEmployeeRole = loggedInEmployee?.user_type;
  const canBackdate = canAddBackdatedExpenses(loggedInEmployeeRole);
  const dynamicOptions = useDynamicOptions(expenseCategoryTypeOptions.data);
  const [addPettyCashExpense, { isLoading, reset: resetAddPettyCashExpense }] =
    useAddPettyCashExpenseMutation();

  const methods = useForm<any>({
    resolver: yupResolver(pettyCashExpenseValidationSchema as any),
    context: { loggedInEmployeeRole },
    defaultValues: initialValues,
    values: initialValues,
    mode: "onChange",
  });

  const { handleSubmit, watch, reset } = methods;
  const values = watch();

  const pettyCashExpenseMeta = {
    title: "Expense Info",
    source: PettyCashExpenseModal,
  };

  const handleReset = () => {
    reset();
    resetAddPettyCashExpense();
  };

  const onSubmitPettyCashForm = async (values: PettyCashFormPayload) => {
    let formattedDate;
    if (values.pettyCashDate) {
      formattedDate = new Date(values.pettyCashDate).toLocaleDateString(
        "en-Ca",
        {
          year: numeric,
          month: TwoDigit,
          day: TwoDigit,
        }
      );
    }

    const expensePettyCashFormPayload = {
      user_id: userId,
      date: formattedDate,
      amount: values?.amount,
      category: values.categoryType,
      details: values?.description,
      payment_type: String(values?.paymentMode) === "1" ? "cash" : "online",
    };

    const expenseFormData = createFormData(expensePettyCashFormPayload);
    expenseFormData.append("attachment", values.pettyCashAttachment[0]);
    const addPettyCashExpenseResponse = await addPettyCashExpense(
      expenseFormData
    );
    formattedDate = "";
    showApiResultAlert(
      addPettyCashExpenseResponse,
      pettyCashExpenseMeta,
      handleReset
    );
  };

  return (
    <FormProvider {...methods}>
      <Form
        className="theme-form"
        onSubmit={handleSubmit(onSubmitPettyCashForm)}
      >
        <Row>
          {pettyCashFormFields.map((field) => {
            if (field.when && !field.when(values)) return null;
            const colSize = field.col ?? 6;

            const options =
              field.dynamicOptions && dynamicOptions[field.dynamicOptions]
                ? dynamicOptions[field.dynamicOptions]
                : field.options ?? [];
            const fieldConfig =
              field.name === "pettyCashDate"
                ? { ...field, minDate: canBackdate ? undefined : monthStart, maxDate: today }
                : field;

            return (
              <Col md={colSize} key={field.name}>
                {DynamicFieldRenderer(fieldConfig, options)}
              </Col>
            );
          })}
        </Row>
        <Row>
          <Col>
            <Btn
              type={submit}
              color={success}
              className="mt-3 f-12"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <IconComponent className="fa fa-spinner fa-spin" />{" "}
                  Loading....
                </>
              ) : (
                Submit
              )}
            </Btn>
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};

export default ManagerZonePettyCashForm;
